'use strict'

// SFCC Hooks
import {useEffect, useState, useMemo} from 'react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useLocation} from 'react-router-dom'
import useMultiSite from './use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAccessToken} from '@salesforce/commerce-sdk-react'

// Custom Hooks
import AlertMgr from '@app/custom-api/alert-custom-api'

/* HOW TO USE THIS HOOK
import {useManualLinking} from '../../hooks/use-manual-linking'
const {manualLinkingResult, isManualLinkingEnabled, isManualLinkingLoading} = useManualLinking(product)
*/

const manualLinkDisabledData = {
    manualLinkingResult: null,
    isManualLinkingLoading: false,
    isManualLinkingEnabled: false
}

/**
 * Custom hook to manage manual linking of products based on color selection and link type.
 * This hook determines if manual linking is enabled for a product and fetches related products
 * that match the criteria for manual linking.
 *
 * @param {Object} product - The product object containing details such as color and link type.
 * @param {string} linkType - The type of link to be used for manual linking.
 * @returns {Object} An object containing:
 *   - {Array} manualLinkingResult: An array of available products that are eligible for manual linking.
 *   - {boolean} isManualLinkingLoading: A flag indicating if the product search is currently loading.
 *   - {boolean} isManualLinkingEnabled: A flag indicating if manual linking is enabled for the product.
 */
export const useManualLinking = (product, linkType) => {
    const {app: appConfig} = getConfig()
    const {getTokenWhenReady} = useAccessToken()
    const {site, locale} = useMultiSite()
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    const selectedColor = urlParams.get('color') || product?.c_color || ''
    const [perfectYourLook, setPerfectYourLook] = useState({})
    const alertMgr = new AlertMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })
    let isManualLinkingEnabled = false

    /**
     * Triggers a cross-sell alert when an error occurs during the parsing of the perfectYourLook JSON for a specific product.
     *
     * @async
     * @param {Object} error - The error object containing details about the error that occurred.
     * @param {string} productId - The identifier of the product for which the error occurred.
     *
     * @returns {Promise<void>} A promise that resolves when the alert has been successfully sent.
     *
     * @description
     * This function checks if both an error and a productId are provided. If so, it retrieves an access token and sends an alert
     * using the alert manager. The alert contains contextual information such as the site ID, error message, category, and other
     * error details like file name, line number, and stack trace. The alert is categorized as a 'technical' alert and does not
     * involve logging.
     */
    async function triggerCrossSellAlert(error, productId) {
        if (!error || !productId) return

        const accessToken = await getTokenWhenReady()
        try {
            await alertMgr.sendAlert(
                {
                    context: {
                        siteId: site?.id,
                        message:
                            'An error occured while parsing perfectYourLook JSON for product ' +
                            product?.id,
                        category: 'CrossSell',
                        order: '',
                        page: '',
                        job: '',
                        asset: '',
                        identifier: productId,
                        fileName: error.fileName || '',
                        lineNumber: error.lineNumber || '',
                        stack: error.stack || ''
                    },
                    alertType: 'business',
                    logging: 'no'
                },
                accessToken
            )
        } catch (error) {
            console.error(
                'An error occured while sending CrossSell alert for the product ' +
                    product?.id +
                    error.message
            )
        }
    }

    useEffect(() => {
        if (product?.[linkType]) {
            try {
                setPerfectYourLook(JSON.parse(product?.[linkType]))
            } catch (error) {
                triggerCrossSellAlert(error, product?.id)
            }
        }
    }, [product, linkType])

    let manualLinkingProductIds = []

    // Check if the product is eligible for manual linking
    if (perfectYourLook[selectedColor]) {
        isManualLinkingEnabled = true

        manualLinkingProductIds = perfectYourLook[selectedColor]
    }

    const {data: productSearchResult, isLoading} = useProducts(
        {
            parameters: {
                ids: manualLinkingProductIds?.join(','),
                perPricebook: true,
                allImages: true,
                expand: ['availability', 'promotions', 'variations', 'prices', 'images', 'options']
            }
        },
        {enabled: isManualLinkingEnabled}
    )

    const availableProducts = useMemo(() => {
        return productSearchResult?.data?.filter((hit) => hit.inventory?.orderable) || []
    }, [productSearchResult])

    if (availableProducts.length === 0 || !isManualLinkingEnabled) {
        return manualLinkDisabledData
    }

    return {
        manualLinkingResult: availableProducts,
        isManualLinkingLoading: isLoading,
        isManualLinkingEnabled: isManualLinkingEnabled
    }
}
